import { Box } from '@mui/material';

import { PageNumberList } from '@common/components/PageNumberList';
import { PaginationArrow } from '@common/components/PaginationArrow';
import {
  CampaignPaginationProps,
  type CampaignPaginationItem,
} from '@common/types/campaign';
import {
  arePageItemsEquivalent,
  buildCampaignPaginationItems,
  CONTROL_SIZE,
  getPageChangeHandler,
  MOBILE_BOUNDARY_COUNT,
  MOBILE_SIBLING_COUNT,
} from '@common/utils/campaignPaginationItems';

/**
 * CampaignPagination
 *
 * Renders the campaign pagination
 * @param disabled - whether pagination controls are disabled while loading
 * @param limit - the limit of the campaigns
 * @param offset - the offset of the campaigns
 * @param onChangePage - the function to change the page
 * @param total - the total number of campaigns
 */
export function CampaignPagination({
  disabled = false,
  limit,
  offset,
  onChangePage,
  total,
}: CampaignPaginationProps) {
  const isPaginationEnabled = limit > 0 && total > 0;
  const pageCount = isPaginationEnabled ? Math.ceil(total / limit) : 0;
  const page = isPaginationEnabled ? Math.floor(offset / limit) + 1 : 1;
  const isSinglePage = pageCount <= 1;
  const isPageChangeDisabled = isSinglePage || disabled;
  const clampedPage = Math.min(Math.max(page, 1), Math.max(pageCount, 1));

  const runPageChange = (newPage: number) => {
    if (isPageChangeDisabled || newPage < 1 || newPage > pageCount) {
      return;
    }

    (document.activeElement as HTMLElement | null)?.blur();
    void onChangePage(newPage);
    window.scrollTo(0, 0);
  };

  if (!isPaginationEnabled) {
    return null;
  }

  const desktopItems = buildCampaignPaginationItems(
    clampedPage,
    pageCount,
    isPageChangeDisabled
  );
  const mobileItems = buildCampaignPaginationItems(
    clampedPage,
    pageCount,
    isPageChangeDisabled,
    MOBILE_SIBLING_COUNT,
    MOBILE_BOUNDARY_COUNT
  );
  const previousItem = desktopItems.find((item) => item.type === 'previous');
  const nextItem = desktopItems.find((item) => item.type === 'next');
  const filterPageItems = (items: CampaignPaginationItem[]) =>
    items.filter(
      (item) =>
        item.type === 'page' ||
        item.type === 'start-ellipsis' ||
        item.type === 'end-ellipsis'
    );
  const desktopPageItems = filterPageItems(desktopItems);
  const mobilePageItems = filterPageItems(mobileItems);
  const hasCompactMobileList = !arePageItemsEquivalent(
    desktopPageItems,
    mobilePageItems
  );

  return (
    <Box
      data-test-id="campaign-pagination"
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'center',
        marginTop: '40px',
        [theme.breakpoints.down('md')]: {
          marginTop: '32px',
        },
      })}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          minHeight: CONTROL_SIZE,
        }}
      >
        {previousItem && (
          <PaginationArrow
            direction="left"
            disabled={previousItem.disabled}
            onClick={getPageChangeHandler(
              previousItem,
              clampedPage,
              isPageChangeDisabled,
              runPageChange
            )}
          />
        )}

        <PageNumberList
          clampedPage={clampedPage}
          isPageChangeDisabled={isPageChangeDisabled}
          listKey="desktop"
          pageItems={desktopPageItems}
          runPageChange={runPageChange}
          sx={
            hasCompactMobileList
              ? { display: { md: 'flex', xs: 'none' } }
              : undefined
          }
        />

        {hasCompactMobileList && (
          <PageNumberList
            clampedPage={clampedPage}
            isPageChangeDisabled={isPageChangeDisabled}
            listKey="mobile"
            pageItems={mobilePageItems}
            runPageChange={runPageChange}
            sx={{ display: { md: 'none', xs: 'flex' } }}
          />
        )}

        {nextItem && (
          <PaginationArrow
            direction="right"
            disabled={nextItem.disabled}
            onClick={getPageChangeHandler(
              nextItem,
              clampedPage,
              isPageChangeDisabled,
              runPageChange
            )}
          />
        )}
      </Box>
    </Box>
  );
}
